<template>
  <ECard v-if="active" class="mockingbird-controls">
    <header class="mockingbird-controls__header">
      <h2 class="mockingbird-controls__title">
        Mockingbird
      </h2>
      <EButton
        class="mockingbird-controls__close"
        icon="close"
        icon-size="s"
        variant="bare"
        size="s"
        @click="close"
      />
    </header>
    <ul class="mockingbird-controls__mocks">
      <li
        v-for="{ id, name, description } in mocks"
        :key="id"
        class="mockingbird-controls__mock"
      >
        <a :href="getMockUrl(id)">
          <h3 class="mockingbird-controls__mock-title">
            {{ name }}
          </h3>
          <p class="mockingbird-controls__mock-description">
            {{ description }}
          </p>
        </a>
      </li>
    </ul>
    <form v-if="customActions" class="mockingbird-controls__buttons">
      <input ref="file-input" type="file" accept="application/json" hidden @change="onFileLoad">
      <EButton
        class="mockingbird-controls__button"
        size="s"
        variant="outline"
        @click="onLoad"
      >
        Load
      </EButton>
      <EButton
        class="mockingbird-controls__button"
        size="s"
        variant="outline"
        @click="onSave"
      >
        Save
      </EButton>
    </form>
  </ECard>
</template>

<script>
import { mapState } from 'vuex';

import EButton from '~~/common/vue/components/button';
import ECard from '~~/common/vue/components/card';

export default {
  name: 'EMockingbirdControls',
  components: {
    EButton,
    ECard,
  },
  props: {
    customActions: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    active: false,
  }),
  computed: {
    ...mapState('mockingbird', ['mocks']),
  },
  mounted() {
    const secondModifier = /Mac/i.test(navigator.platform) ? 'shiftKey' : 'altKey';
    this.keyDownListener = (event) => {
      if (event?.key.toLowerCase() === 'm' && event.ctrlKey && event[secondModifier]) {
        event.preventDefault();
        this.active = !this.active;
      }
    };
    document.addEventListener('keydown', this.keyDownListener);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.keyDownListener);
  },
  methods: {
    close() {
      this.active = false;
    },
    getMockUrl(id) {
      const query = { ...this.$route.query, mocked: id };
      const { href } = this.$router.resolve({ query });
      return href;
    },
    onFileLoad({ target: { files: [file] } }) {
      const fileReader = new FileReader();

      fileReader.addEventListener('load', () => {
        const mocks = JSON.parse(fileReader.result);
        // TODO: currently expects this works with search/web only; make it agnostic
        this.$store.dispatch('populateResults', { results: mocks });
      });

      fileReader.readAsText(file);
    },
    onLoad() {
      // eslint-disable-next-line no-unused-expressions
      this.$refs['file-input']?.click();
    },
    onSave() {
      // TODO: currently expects this works with search/web only; make it agnostic
      const text = JSON.stringify(this.$store.getters.results, null, 2);
      const blob = new Blob([text], { type: 'application/json' });
      const anchor = document.createElement('a');
      anchor.href = URL.createObjectURL(blob);
      anchor.download = `ecosia-results-${new Date().toISOString()}.json`;
      anchor.click();
    },
  },
};
</script>

<style lang="scss" scoped>
// TODO: move this to common/scss if reused
@keyframes e-slide-bottom {
  from {
    transform: translateY($slide-offset);
  }

  to {
    transform: translateY(0);
  }
}

.mockingbird-controls__header,
.mockingbird-controls__mocks,
.mockingbird-controls__buttons {
  padding: $space-s $space-m;
}

.mockingbird-controls {
  @include elevation(2);

  animation: e-fade-in $timing-1s forwards, e-slide-bottom $timing-1s forwards;
  background: var(--color-background-primary);
  bottom: $space-l;
  max-width: 600px;
  position: fixed;
  right: $space-l;
  z-index: $z-index-overlay;
}

.mockingbird-controls__header {
  @include divider('bottom');

  display: flex;
}

.mockingbird-controls__close {
  margin-left: auto;
  margin-right: 0;
}

.mockingbird-controls__title {
  color: var(--color-text-secondary);
  font-size: $font-m;
  font-weight: $font-weight-m;
  margin: 0;
}

.mockingbird-controls__title-icon {
  margin-right: $space-1s;
}

.mockingbird-controls__mocks {
  list-style-type: none;
  margin: 0;
  max-height: 500px;
  overflow-y: auto;
  display: grid;
  grid-template-columns: 50%;
  grid-template-areas: 'item item';
  grid-gap: $space-s;
}

.mockingbird-controls__mock-title {
  font-weight: $font-weight-m;
  margin-bottom: 0;
  color: var(--color-brand-primary);
  text-decoration: underline;
}

.mockingbird-controls__mock {
  .mockingbird-controls__mock-title {
    margin-top: 0;
  }
}

.mockingbird-controls__mock-description {
  margin: 0;
}

.mockingbird-controls__buttons {
  @include divider('top');

  display: flex;
}

.mockingbird-controls__button {
  & + & {
    margin-left: $space-s;
  }
}
</style>
